import { authConfig } from '../features/auth/authConfig';
import { authService } from '../features/auth/authService';

// Basis-URL met fallbacks (authConfig -> Vite -> CRA)
const RESOLVED_BASE =
  (authConfig.apiBaseUrl && authConfig.apiBaseUrl.trim()) ||
  ((typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_BASE_URL) as string) ||
  process.env.REACT_APP_API_BASE_URL ||
  '';

console.log('[DEBUG] RESOLVED_BASE:', RESOLVED_BASE);

const BASE_URL = (RESOLVED_BASE || '').replace(/\/+$/, '');

function apiUrl(path: string) {
  const url = BASE_URL ? `${BASE_URL}${path}` : path;
  console.log('[DEBUG] apiUrl result:', url);
  return url;
}

// Log eenmaal zodat je zeker bent waarheen het gaat
if (typeof window !== 'undefined') {
  console.info('[API] BASE_URL =', BASE_URL || '(relative to frontend origin)');
}

// --- Types (exact zoals RN) ---
export interface ChatSessionSummary { id: string; creationTime: string; name: string; }
export interface ContextItem {
  id: string;
  content: string;
  fileId: string;
  fileName: string;
  title?: string;
  description?: string;
  language?: string;
  url?: string;
  displayUrl?: string;
  sourcePath?: string;
  sourceUrl?: string;
  productName?: string;
  documentType?: string;
  pageNumber?: number;
  citationIndex?: number;
}
export interface ChatHistoryItem { id: string; parentId?: string; sessionId: string; role: string; message: string; timestamp?: string; contexts?: ContextItem[]; }
export interface ChatResponse {
  userChatHistoryItemId?: string; chatHistoryItemId?: string; chatSessionId?: string; chatSessionName?: string;
  response: string; contexts: ContextItem[];
}
export interface ChatFeedbackRequest { chatHistoryItemId: string; isPositive: boolean; comment?: string; }
export interface ChatFeedback { id: string; chatHistoryItemId: string; isPositive: boolean; comment?: string; timestamp: string; }
export interface FilterResponse { [key: string]: any; }

// Backend types uit RN
export interface RunRequest {
  agentName: string;
  input: unknown[];
  mode?: "sync" | "stream";
  threadId?: string | null;
  resumeFromMessageId?: string | null;
}

export interface BackendPart {
  type?: string;
  messageId?: string;
  contentType?: string;
  content?: string;
  contentEncoding?: string;
  metadata?: MetadataItem[];
  toolId?: string;
  toolName?: string;
  toolArgs?: Record<string, unknown>;
  toolResult?: unknown;
  displayMessage?: string;
  [key: string]: unknown;
}

export interface BackendMessage {
  role?: string;
  parts?: BackendPart[];
  createdAt?: string;
  completedAt?: string;
  [key: string]: unknown;
}

export interface RunResponse {
  runId: string;
  agentName: string;
  threadId: string;
  status: string;
  output?: BackendMessage[];
  error?: unknown;
  thread_metadata?: unknown;
}

export interface DocumentData {
  id: string;
  indexName?: string;
  language?: string;
  title?: string;
  content?: string;
  fileId?: string;
  fileName?: string;
  displayUrl?: string;
  sourceId?: string;
  sourcePath?: string;
  sourceUrl?: string;
  pageNumber?: number;
  extraFields?: { productName?: string; documentType?: string; [key: string]: unknown; };
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface MetadataItem {
  kind: string;
  url?: string;
  title?: string;
  description?: string;
  displayIndex?: number;
  document?: DocumentData;
  [key: string]: unknown;
}

export interface ThreadInfo {
  threadId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

export interface ThreadHistoryResponse {
  threadId: string;
  messages: BackendMessage[];
}

export interface SubmitFeedbackRequest {
  isPositive: boolean;
  comment?: string | null;
}

export interface FeedbackResponse {
  id: string;
  thread_id: string;
  message_id: string;
  user_id: string;
  is_positive: boolean;
  comment?: string | null;
  created_at: string;
  updated_at: string;
}

// --- Helpers ---
function decodeJwt(token?: string) {
  try {
    if (!token) return undefined;
    const payload = token.split('.')[1];
    return JSON.parse((globalThis.atob ?? window.atob)(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch { return undefined; }
}
function hasChatbotScope(token?: string) {
  const c = decodeJwt(token);
  const scopes = (c?.scope ?? c?.scp ?? '') as string | string[];
  const list = Array.isArray(scopes) ? scopes : (scopes ? scopes.split(' ') : []);
  return list.includes('chatbot');
}

async function fetchJson<T>(url: string, init: RequestInit = {}, timeoutMs = 30000): Promise<T> {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeoutMs);
  const token = authService.getAccessToken();
  const method = (init.method || 'GET').toUpperCase();

  if (token) {
    const decoded = decodeJwt(token);
    console.log('[AUTH DEBUG] Token payload:', decoded);
  }

  if (!token || !hasChatbotScope(token)) {
    throw new Error('Geen geldig API-toegangstoken met scope "chatbot". Log opnieuw in.');
  }

  const headers: HeadersInit = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    ...init.headers,
  };

  // Log request zoals RN voorbeeld
  if (method === 'POST' && init.body) {
    try {
      const bodyData = JSON.parse(init.body as string);
      console.log('Creating run with request:', JSON.stringify(bodyData, null, 2));
    } catch { /* ignore */ }
  }

  try {
    const res = await fetch(url, { ...init, headers, signal: controller.signal });
    clearTimeout(t);

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`[HTTP ${res.status}] ${method} ${url}\n${res.statusText}${text ? `\n${text}` : ''}`);
    }
    if (res.status === 204) return undefined as unknown as T;
    
    const result = await res.json();
    
    // Log response zoals RN voorbeeld
    if (method === 'POST') {
      console.log('Run created with response:', JSON.stringify(result, null, 2));
    }
    
    return result as T;
  } catch (e: any) {
    clearTimeout(t);
    if (e?.name === 'AbortError') {
      throw new Error(`Time-out na ${Math.round(timeoutMs / 1000)}s bij ${method} ${url}. Mogelijke oorzaken: server traag of niet bereikbaar.`);
    }
    const msg = String(e?.message || e);
    if (/failed to fetch|load failed/i.test(msg)) {
      const hints = [
        '- Controleer CORS (preflight OPTIONS) en of Authorization-header toegestaan is.',
        '- Klopt de API URL en is deze bereikbaar vanaf deze omgeving?',
        '- Mixed content? Gebruik overal https.',
      ];
      throw new Error(`${method} ${url} mislukt: ${msg}\n${hints.join('\n')}`);
    }
    throw new Error(`${method} ${url} mislukt: ${msg}`);
  }
}

export interface RunProgressPayload {
  event: string;
  part?: BackendPart;
  data?: any;
  output?: BackendMessage[] | any;
  final?: RunResponse | any;
  lastData?: any;
}

function createStreamingRun(
  runRequest: RunRequest,
  onProgress: (payload: RunProgressPayload) => void,
  abortSignal?: AbortSignal | null
): Promise<RunResponse> {
  return new Promise<RunResponse>((resolve, reject) => {
    const token = authService.getAccessToken();

    if (!token || !hasChatbotScope(token)) {
      reject(new Error('Geen geldig API-toegangstoken met scope "chatbot". Log opnieuw in.'));
      return;
    }

  const controller = new AbortController();
  let finished = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const messageBuffers: Record<string, { parts: BackendPart[]; mergedContent: string }> = {};
    const buildOutputFromBuffers = () =>
      Object.keys(messageBuffers).map((mid) => {
        const buffer = messageBuffers[mid];
        const mergedMetadata = buffer.parts.flatMap((p) => p.metadata ?? []);
        return {
          role: 'agent/assistant',
          parts: [
            {
              ...buffer.parts[0],
              content: buffer.mergedContent,
              metadata: mergedMetadata.length ? mergedMetadata : buffer.parts[0].metadata,
            },
          ],
        };
      });

    const ensureString = (value: unknown): string => {
      const coerce = (val: unknown): string => {
        if (val === null || val === undefined) return '';
        if (typeof val === 'string') return val;
        if (typeof val === 'number' || typeof val === 'boolean') return String(val);

        if (Array.isArray(val)) {
          return val.map((item) => coerce(item)).join('');
        }

        if (typeof val === 'object') {
          const obj = val as Record<string, unknown>;
          if (typeof obj.text === 'string') return obj.text;
          if (typeof (obj.text as any)?.value === 'string') return (obj.text as any).value as string;
          if (typeof obj.value === 'string') return obj.value as string;
          if (Array.isArray(obj.content)) return obj.content.map((item) => coerce(item)).join('');
        }

        try {
          return JSON.stringify(val);
        } catch {
          return String(val);
        }
      };

      return coerce(value);
    };

    const normalizePart = (input: any, fallbackType?: string): BackendPart | null => {
      if (!input || typeof input !== 'object') return null;
      const messageId =
        input.messageId ??
        input.message_id ??
        input.message?.messageId ??
        input.message?.message_id ??
        input.message?.id ??
        input.id ??
        null;

      const rawContent =
        input.content ??
        input.text ??
        input.delta?.text ??
        input.delta?.content ??
        input.displayMessage ??
        input.toolResult ??
        null;

      const content = ensureString(rawContent);

      const normalized: BackendPart = {
        ...(input as BackendPart),
        messageId: messageId ?? undefined,
        content,
      };

      if (!normalized.type && fallbackType) {
        normalized.type = fallbackType;
      }

      if (!normalized.content && typeof input.displayMessage === 'string') {
        normalized.content = input.displayMessage;
      }

      return normalized;
    };

    const appendPartFromEvent = (eventType: string, partLike: any) => {
      const normalized = normalizePart(partLike, eventType === 'message.delta' ? 'message' : undefined);
      if (!normalized) return;
      const messageId = normalized.messageId || '__default_message';
      normalized.messageId = messageId;
      if (!normalized.content) return;

      const prev = messageBuffers[messageId] || { parts: [], mergedContent: '' };
      prev.parts.push(normalized);
      prev.mergedContent += normalized.content;
      messageBuffers[messageId] = prev;
      lastData.output = buildOutputFromBuffers();

      try {
        console.log('[SSE]', eventType, normalized);
      } catch {}

      onProgress({
        event: eventType,
        part: normalized,
        output: lastData.output,
        lastData,
      });
    };

    let lastData: RunResponse = {
      runId: '',
      agentName: runRequest.agentName,
      threadId: runRequest.threadId || '',
      status: 'streaming',
    };

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (abortSignal) {
        abortSignal.removeEventListener('abort', onAbort);
      }
    };

    const finish = (data?: RunResponse) => {
      if (finished) return;
      finished = true;
      cleanup();

      if (Object.keys(messageBuffers).length) {
        const out = buildOutputFromBuffers();
        const target = data || lastData;
        target.output = out;
        resolve(target);
        return;
      }

      resolve(data || lastData);
    };

    const fail = (error: any) => {
      if (finished) return;
      finished = true;
      cleanup();
      reject(error);
    };

    const onAbort = () => {
      controller.abort();
      finish();
    };

    if (abortSignal) {
      if (abortSignal.aborted) {
        finish();
        return;
      }
      abortSignal.addEventListener('abort', onAbort, { once: true });
    }

    const headers: HeadersInit = {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'Cache-Control': 'no-cache',
    };

    const url = apiUrl('/runs');

    fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ...runRequest, mode: 'stream' }),
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(`[HTTP ${res.status}] POST ${url}\n${res.statusText}${text ? `\n${text}` : ''}`);
        }

        if (!res.body) {
          throw new Error('Streaming response bevat geen body.');
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        const dispatchEvent = (eventType: string, rawData: string) => {
          let parsed: any = null;
          try {
            const cleaned = rawData.replace(/\r/g, '').trim();
            parsed = cleaned ? JSON.parse(cleaned) : null;
          } catch (err) {
            console.error('[SSE] Kon data niet parsen:', rawData, err);
            return;
          }

          if (!eventType) {
            console.warn('[SSE] Ontvangen bericht zonder event type:', parsed);
            return;
          }

          if (eventType === 'message.part') {
            appendPartFromEvent(eventType, parsed.part ?? parsed);
            return;
          }

          if (eventType === 'message.delta') {
            appendPartFromEvent(eventType, parsed.delta ?? parsed.part ?? parsed);
            return;
          }

          if (eventType === 'thread.title.updated' && parsed.thread) {
            lastData = {
              ...lastData,
              threadId: parsed.thread.threadId || lastData.threadId,
            };
            onProgress({ event: eventType, data: parsed, output: lastData.output, lastData });
            return;
          }

          if (eventType === 'run.started') {
            lastData = {
              ...lastData,
              runId: parsed.run?.runId || lastData.runId,
              status: parsed.run?.status || lastData.status,
            };
            onProgress({ event: eventType, data: parsed, output: lastData.output, lastData });
            return;
          }

          if (eventType === 'run.completed' || eventType === 'run.failed' || eventType === 'run.cancelled') {
            const finalData: RunResponse = {
              runId: parsed.run?.runId || lastData.runId,
              agentName: parsed.run?.agentName || lastData.agentName,
              threadId: parsed.run?.threadId || lastData.threadId,
              status: parsed.run?.status || eventType,
              output: parsed.run?.output,
              error: parsed.run?.error,
            };

            if (!finalData.output && Object.keys(messageBuffers).length) {
              finalData.output = buildOutputFromBuffers();
            }

            onProgress({ event: eventType, final: finalData, lastData });
            finish(finalData);
            return;
          }

          onProgress({ event: eventType, data: parsed, output: lastData.output, lastData });
        };

        const extractNextEvent = (): { rawEvent: string; separatorLength: number } | null => {
          const idxCRLF = buffer.indexOf('\r\n\r\n');
          const idxLF = buffer.indexOf('\n\n');

          let sepIndex = -1;
          let sepLength = 0;

          if (idxCRLF !== -1 && (idxLF === -1 || idxCRLF <= idxLF)) {
            sepIndex = idxCRLF;
            sepLength = 4;
          } else if (idxLF !== -1) {
            sepIndex = idxLF;
            sepLength = 2;
          }

          if (sepIndex === -1) {
            return null;
          }

          const rawEvent = buffer.slice(0, sepIndex);
          buffer = buffer.slice(sepIndex + sepLength);
          return { rawEvent, separatorLength: sepLength };
        };

        const processBuffer = () => {
          let next: { rawEvent: string; separatorLength: number } | null;
          while ((next = extractNextEvent())) {
            const rawEvent = next.rawEvent.replace(/\r/g, '');
            if (!rawEvent.trim()) continue;
            try {
              console.log('[SSE RAW]', rawEvent);
            } catch {}
            const lines = rawEvent.split('\n');
            let eventType = '';
            const dataLines: string[] = [];
            for (const line of lines) {
              const trimmed = line.trimEnd();
              if (trimmed.startsWith('event:')) {
                eventType = trimmed.slice(6).trim();
              } else if (trimmed.startsWith('data:')) {
                dataLines.push(trimmed.slice(5).trimStart());
              }
            }
            const dataStr = dataLines.join('\n');
            try {
              console.log('[SSE PARSED]', { eventType, dataStr });
            } catch {}
            dispatchEvent(eventType, dataStr);
          }
        };

        const pump = async (): Promise<void> => {
          try {
            while (!finished) {
              const { done, value } = await reader.read();
              if (done) break;
              if (value) {
                buffer += decoder.decode(value, { stream: true });
                processBuffer();
              }
            }
          } catch (err) {
            fail(err);
            return;
          }
          if (buffer.length) {
            processBuffer();
          }
          if (!finished) {
            finish();
          }
        };

        pump().catch((err) => fail(err));
      })
      .catch((err) => {
        if (err?.name === 'AbortError') {
          finish();
          return;
        }
        fail(err);
      });

    timeoutId = setTimeout(() => {
      if (!finished) {
        console.warn('[SSE] Timeout op streaming-run');
        controller.abort();
        finish();
      }
    }, 120000);
  });
}

// --- Thread/Run API functies (exact zoals RN) ---
export function createRun(
  runRequest: RunRequest,
  onProgress?: (payload: RunProgressPayload) => void,
  abortSignal?: AbortSignal | null
): Promise<RunResponse> {
  if (runRequest.mode === 'stream' && onProgress) {
    return createStreamingRun(runRequest, onProgress, abortSignal);
  }

  return fetchJson<RunResponse>(
    apiUrl('/runs'),
    {
      method: 'POST',
      body: JSON.stringify({ ...runRequest, mode: 'sync' }),
    },
    60000
  );
}

export function listUserThreads(userId: string): Promise<ThreadInfo[]> {
  const url = apiUrl(`/threads/users/${encodeURIComponent(userId)}/threads`);
  console.log("Fetching threads from URL:", url);
  return fetchJson<{ threads: ThreadInfo[] }>(url).then(r => {
    console.log("Fetched threads data:", r);
    return r.threads;
  });
}

export function createThread(userId: string, title = 'New Conversation'): Promise<ThreadInfo> {
  const url = apiUrl(`/threads/users/${encodeURIComponent(userId)}/threads`);
  return fetchJson<ThreadInfo>(url, { method: 'POST', body: JSON.stringify({ title }) });
}

export function updateThreadTitle(threadId: string, title: string): Promise<ThreadInfo> {
  const encoded = encodeURIComponent(threadId);
  const url = apiUrl(`/threads/${encoded}/title`);
  return fetchJson<ThreadInfo>(url, { method: 'PATCH', body: JSON.stringify({ title }) }).then(result => {
    console.log("Updated thread title:", result);
    return result;
  });
}

export function deleteThread(threadId: string): Promise<ThreadInfo> {
  const encoded = encodeURIComponent(threadId);
  const url = apiUrl(`/threads/${encoded}`);
  return fetchJson<ThreadInfo>(url, { method: 'DELETE' });
}

export function getThreadHistory(threadId: string): Promise<ThreadHistoryResponse> {
  const encoded = encodeURIComponent(threadId);
  const url = apiUrl(`/threads/${encoded}/messages`);
  console.log("Fetching thread history from URL:", url);
  return fetchJson<ThreadHistoryResponse>(url).then(result => {
    console.log("Fetched thread history data:", JSON.stringify(result, null, 2));
    return result;
  });
}

export function submitThreadFeedback(threadId: string, messageId: string, request: SubmitFeedbackRequest): Promise<FeedbackResponse> {
  const encodedThread = encodeURIComponent(threadId);
  const encodedMessage = encodeURIComponent(messageId);
  const url = apiUrl(`/threads/${encodedThread}/messages/${encodedMessage}/feedback`);
  console.log("Submitting feedback to URL:", url, "with request:", request);
  return fetchJson<FeedbackResponse>(url, { method: 'POST', body: JSON.stringify(request) });
}

// --- Chat API functies (aangepast naar RN structuur) ---
export function chat(
  data: any,
  onProgress?: (payload: RunProgressPayload) => void,
  abortSignal?: AbortSignal | null
): Promise<ChatResponse> {
  // data: { userId, sessionId, message: { parentId, message, role: 'user' } }
  
  return (async () => {
    let threadId = data?.sessionId;
    
    // Als geen sessionId, maak een nieuwe thread aan
    if (!threadId && data?.userId) {
      console.log('No sessionId provided, creating new thread for user:', data.userId);
      const newThread = await createThread(data.userId, 'New Conversation');
      threadId = newThread.threadId;
      console.log('Created new thread:', threadId);
    }
    
    // Als nog steeds geen threadId, error
    if (!threadId) {
      throw new Error('Geen threadId beschikbaar en geen userId om nieuwe thread aan te maken');
    }

    // Converteer naar exact dezelfde RunRequest als RN
    const runReq: RunRequest = {
      agentName: 'boma_agent', // <-- Gebruik dezelfde agent als RN
      input: [
        {
          role: 'user',
          parts: [
            {
              type: 'message', // <-- Correct type zoals API verwacht
              message_id: `${Date.now()}`, // <-- Gebruik message_id zoals RN
              content: data?.message?.message || '',
              content_type: 'text/plain' // <-- Gebruik content_type zoals RN
            }
          ]
        }
      ],
      mode: onProgress ? 'stream' : 'sync',
      threadId: threadId, // <-- Nu altijd een geldige threadId
      resumeFromMessageId: null, // RN gebruikt null
    };

    return createRun(runReq, onProgress, abortSignal).then((r: RunResponse) => {
      if (r.error) {
        throw new Error((r.error as any)?.message || 'Run mislukt');
      }

      const toText = (part?: BackendPart) => {
        if (!part) return '';
        const raw = part.content ?? part.displayMessage ?? '';
        if (typeof raw === 'string') return raw;
        try {
          return JSON.stringify(raw);
        } catch {
          return String(raw);
        }
      };

      const out = r.output && r.output.length > 0 ? r.output[r.output.length - 1] : undefined;
      const parts = out?.parts || [];
      const messagePart = parts.find((p) => p.type === 'message') || parts[0];
      const outText = parts.length ? parts.map((p) => toText(p)).join('') : '';
      
      // Extract contexts from metadata zoals RN
      const contexts: ContextItem[] = [];
      if (messagePart && Array.isArray(messagePart.metadata) && messagePart.metadata.length) {
        messagePart.metadata.forEach((meta) => {
          if (meta.kind === 'citation' && meta.document) {
            contexts.push({
              id: meta.document.id || '',
              content: meta.document.content || '',
              fileId: meta.document.fileId || '',
              fileName: meta.document.fileName || '',
              title: meta.title || meta.document.title,
              description: meta.description,
              language: meta.document.language,
              url: meta.url,
              displayUrl: meta.document.displayUrl || meta.url,
              sourcePath: meta.document.sourcePath,
              sourceUrl: meta.document.sourceUrl || meta.url,
              productName: meta.document.extraFields?.productName,
              documentType: meta.document.extraFields?.documentType,
              pageNumber: meta.document.pageNumber,
              citationIndex: typeof meta.displayIndex === 'number' ? meta.displayIndex : undefined,
            });
          }
        });
      }

      return {
  response: toText(messagePart) || outText,
        contexts,
        chatSessionId: r.threadId, // Return de threadId (kan nieuw aangemaakt zijn)
        chatHistoryItemId: r.runId,
        chatSessionName: '',
      } as ChatResponse;
    });
  })();
}

// Map oude session calls naar thread calls (backwards compatibility)
export function getUserChatSessions(userId: string): Promise<ChatSessionSummary[]> {
  return listUserThreads(userId).then(threads =>
    threads.map(t => ({ id: t.threadId, creationTime: t.createdAt, name: t.title }))
  );
}

export function createUserChatSession(userId: string): Promise<ChatSessionSummary> {
  return createThread(userId).then(t => ({ id: t.threadId, creationTime: t.createdAt, name: t.title }));
}

export function updateChatSessionName(sessionId: string, name: string): Promise<ChatSessionSummary> {
  return updateThreadTitle(sessionId, name).then(t => ({ id: t.threadId, creationTime: t.createdAt, name: t.title }));
}

export function deleteChatSession(sessionId: string): Promise<ChatSessionSummary> {
  return deleteThread(sessionId).then(t => ({ id: t.threadId, creationTime: t.createdAt, name: t.title }));
}

export function getSessionChatHistory(sessionId: string): Promise<ChatHistoryItem[]> {
  return getThreadHistory(sessionId).then(({ threadId, messages }) => {
    const flat: ChatHistoryItem[] = [];
    messages.forEach((m, idx) => {
      const parts = m.parts || [];
      const toText = (part?: BackendPart) => {
        if (!part) return '';
        const raw = part.content ?? part.displayMessage ?? '';
        if (typeof raw === 'string') return raw;
        try {
          return JSON.stringify(raw);
        } catch {
          return String(raw);
        }
      };
      const messagePart = parts.find((p) => p.type === 'message') || parts[0];
      const text = parts.length ? parts.map((p) => toText(p)).join('') : '';
      flat.push({
        id: `${threadId}-${idx}`,
        sessionId: threadId,
        role: m.role ?? 'assistant',
        message: toText(messagePart) || text,
        timestamp: m.createdAt,
        contexts: [],
      });
    });
    return flat;
  });
}

export function getChatFilters(): Promise<FilterResponse> {
  return fetchJson<FilterResponse>(apiUrl(`/chat/filters`));
}

export function submitChatFeedback(messageId: string, type: string, feedback: ChatFeedbackRequest): Promise<ChatFeedback> {
  return Promise.reject(new Error('submitChatFeedback is vervangen. Gebruik submitThreadFeedback(threadId, messageId, request).'));
}